import { ConfigColors } from 'libs/end-to-end/src/types/branding/colors';
import { toPng } from 'html-to-image';
import { errResult } from 'libs/end-to-end/src/result';
import { okResult } from 'libs/end-to-end/src/result';
import { Result } from 'libs/end-to-end/src/result';
import { ElementToImageDataError } from 'libs/end-to-end/src/types/errors/element-to-image-data-error';

const filter = (node: HTMLElement) => {
  const exclusionClasses = ['html-to-image-filter'];
  return !exclusionClasses.some((classname) =>
    node.classList?.contains(classname),
  );
};

/**
 * Removes the opacity from the element.
 * @param element - The element to remove the opacity from
 * @returns void
 */
const removeOpacity = (element: Element) => {
  const stroke = element.getAttribute('stroke');
  const fill = element.getAttribute('fill');
  if (stroke && stroke !== 'none') {
    // Remove the opacity from the stroke
    element.setAttribute('stroke', stroke.replace('60', ''));
  }
  if (fill && fill !== 'none') {
    // Remove the opacity from the fill
    element.setAttribute('fill', fill.replace('60', ''));
  }
};

/**
 * Adds the opacity to the element.

 * @param element - The element to add the opacity to
 * @returns void
 */
const addOpacity = (element: Element) => {
  const stroke = element.getAttribute('stroke');
  const fill = element.getAttribute('fill');
  if (stroke && stroke !== 'none') {
    // Remove the opacity from the stroke
    element.setAttribute('stroke', stroke + '60');
  }
  if (fill && fill !== 'none') {
    // Remove the opacity from the fill
    element.setAttribute('fill', fill + '60');
  }
};

/**
 * This is a preprocess step to remove the opacity from the
 * bin scatter multi select chart before downloading. This makes the chart
 * look better when downloaded with lines and points being more visible.
 * @param element - The element to preprocess
 * @returns void
 */
const preprocessBinScatterMultiselectChart = (graphSvgElement: Element) => {
  // This handles opacity from both lines and the scatter icons but
  // skips over the lines and points for the RaceWhite or GenderMale demographic.
  // This is because both of those demographics are the primary demographics and
  // should be highlighted in the chart already.
  const skipColor = ConfigColors.Blue.Primary.raw;
  if (graphSvgElement) {
    const points = graphSvgElement.getElementsByTagName('path');
    const circles = graphSvgElement.getElementsByTagName('circle');
    for (let i = 0; i < points.length; i++) {
      const point = points[i];
      const stroke = point.getAttribute('stroke');
      const fill = point.getAttribute('fill');
      if (stroke !== skipColor && fill !== skipColor) {
        removeOpacity(point);
      }
    }

    for (let i = 0; i < circles.length; i++) {
      const circle = circles[i];
      const stroke = circle.getAttribute('stroke');
      const fill = circle.getAttribute('fill');
      if (stroke !== skipColor && fill !== skipColor) {
        removeOpacity(circle);
      }
      removeOpacity(circle);
    }
  }
};

/**
 * This is a postprocess step to add the opacity back to the
 * bin scatter multi select chart after downloading.
 * @param graphSvgElement
 * @returns void
 */
const postprocessBinScatterMultiselectChart = (graphSvgElement: Element) => {
  // Reset the opacity of the lines and points
  // Refer to line 62 for more information.
  const skipColor = ConfigColors.Blue.Primary.raw;
  if (graphSvgElement) {
    const points = graphSvgElement.getElementsByTagName('path');
    const circles = graphSvgElement.getElementsByTagName('circle');
    for (let i = 0; i < points.length; i++) {
      const point = points[i];
      const stroke = point.getAttribute('stroke');
      const fill = point.getAttribute('fill');
      if (stroke !== skipColor && fill !== skipColor) {
        addOpacity(point);
      }
    }

    for (let i = 0; i < circles.length; i++) {
      const circle = circles[i];
      const stroke = circle.getAttribute('stroke');
      const fill = circle.getAttribute('fill');
      if (stroke !== skipColor && fill !== skipColor) {
        addOpacity(circle);
      }
    }
  }
};

/**
 * Download the image of the chart as PNG.
 * @param element - The element to download
 * @param chartName - The name of the chart
 * @returns void
 */
export const handleDownloadImage = async (
  element: HTMLElement | null,
  chartName: string,
) => {
  const convertResult = await elementToImageData({
    includeEffects: true,
    element,
  });
  if (convertResult.resultType === 'err') {
    return;
  }

  const link = document.createElement('a');
  link.download = `${chartName}.png`;
  link.href = convertResult.value;
  link.click();
  link.remove();
};

export const elementToImageData = async ({
  element,
  includeEffects,
}: {
  element: HTMLElement | null;
  includeEffects: boolean;
}): Promise<Result<string, ElementToImageDataError>> => {
  if (!element) {
    return errResult({
      errorType: 'ElementToImageDataError',
      errorReason: 'Element is null',
    });
  }

  const svgElements = element.getElementsByTagName('svg');
  let graphSvgElement: Element | null = null;

  for (let i = 0; i < svgElements.length; i++) {
    if (svgElements[i].getAttribute('role') === 'img') {
      graphSvgElement = svgElements[i];
      break;
    }
  }

  if (
    includeEffects &&
    element.id.includes('bin-scatter-multi-select') &&
    graphSvgElement
  ) {
    preprocessBinScatterMultiselectChart(graphSvgElement);
  }

  try {
    let data: string | null = null;

    try {
      data = await toPng(element, {
        filter,
        backgroundColor: '#f7f7f7',
        pixelRatio: 2,
        style: {
          // Adds a little padding to the exported image. Padding doesn't work here
          transform: `scale(.90, .95)`,
        },
      });
    } catch {
      return errResult({
        errorType: 'ElementToImageDataError',
        errorReason: 'Failed to convert element to image',
      });
    }

    if (!data) {
      return errResult({
        errorType: 'ElementToImageDataError',
        errorReason: 'Failed to convert element to image',
      });
    }

    return okResult(data);
  } finally {
    if (
      includeEffects &&
      element.id.includes('bin-scatter-multi-select') &&
      graphSvgElement
    ) {
      postprocessBinScatterMultiselectChart(graphSvgElement);
    }
  }
};
